/**
 * Selection of the competency sections a collaborator has to answer, given the
 * response of `POST /collaborator_tests/disc_color_pair`.
 *
 * The DISC pattern no longer lives in `test_sections.title` (it moved to the
 * competency catalog in the backend), so the join key with the catalog is
 * `test_sections.description` — the competency name. The endpoint therefore
 * returns `competences` alongside the legacy `sectionTitles`.
 */

export interface TestSection {
  title?: string;
  description?: string;
}

export interface DiscColorPairResponse {
  /** Legacy: `test_sections.title` of the sections of the predominant pair. */
  sectionTitles?: string[];
  /** Competency names, matching `test_sections.description`. */
  competences?: string[];
}

const isNonEmptyArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length > 0;

/**
 * Picks the competency sections to serve.
 *
 * Filters by `description` against `competences`. Falls back to the legacy
 * filter by `title` against `sectionTitles` when `competences` is missing
 * (a new front deployed against an older backend) or when it matches no
 * section: leaving the collaborator with no competencies at all is worse than
 * using the previous mechanism, which yields the very same sections.
 */
export const selectCompetencySections = <T extends TestSection>(
  competencySections: T[],
  response: DiscColorPairResponse | null | undefined,
): T[] => {
  const sections = Array.isArray(competencySections) ? competencySections : [];
  const competences = response?.competences;
  const sectionTitles = response?.sectionTitles;

  if (isNonEmptyArray(competences)) {
    const byDescription = sections.filter(
      (section) => !!section.description && competences.includes(section.description),
    );
    if (byDescription.length > 0) return byDescription;
  }

  if (isNonEmptyArray(sectionTitles)) {
    return sections.filter((section) => !!section.title && sectionTitles.includes(section.title));
  }

  return [];
};

/** Modo "competencias específicas" (#16): las 8 se eligieron al enviar el test. */
export const isSpecificCompetencyMode = (
  selectedCompetences: unknown,
): selectedCompetences is string[] => isNonEmptyArray(selectedCompetences);

/**
 * Secciones a servir en modo específicas.
 *
 * Se reusa `selectCompetencySections` porque `selectedCompetences` guarda los
 * mismos `test_sections.description` que devuelve el endpoint DISC en
 * `competences` — no hace falta un segundo filtro.
 *
 * A diferencia del modo por patrón, acá se exige un calce completo. Ese filtro
 * devuelve lo que haya calzado aunque sea un subconjunto, lo que en modo por
 * patrón no puede pasar (ambos lados salen del mismo catálogo), pero acá sí: los
 * nombres los eligió una persona y quedaron guardados al enviar el test, así que
 * un calce parcial —una competencia renombrada, por ejemplo— evaluaría menos
 * competencias de las solicitadas, en silencio.
 */
export const selectSpecificCompetencySections = <T extends TestSection>(
  competencySections: T[],
  selectedCompetences: string[],
): T[] => {
  const sections = selectCompetencySections(competencySections, {
    competences: selectedCompetences,
  });

  if (sections.length !== selectedCompetences.length) {
    throw new Error(
      `Se seleccionaron ${selectedCompetences.length} competencias pero sólo se resolvieron ${sections.length} secciones`,
    );
  }

  return sections;
};
